import { z } from 'zod';
import type { pluginManifestSchema } from './tools';

export const MARKETPLACE_CATEGORIES = [
  'Developer tools',
  'Productivity',
  'Data & research',
  'Communication',
  'Commerce',
  'Utilities',
] as const;
export const marketplaceCategorySchema = z.enum(MARKETPLACE_CATEGORIES);
export type MarketplaceCategory = z.infer<typeof marketplaceCategorySchema>;

const hexColor = z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Use a #RRGGBB colour');

/**
 * A value the admin supplies when installing (e.g. a Shopify store domain). Manifest strings
 * reference it as `{{param.<key>}}`; it is checked against `pattern` before substitution.
 */
export const marketplaceParameterSchema = z.object({
  key: z.string().regex(/^[a-zA-Z][a-zA-Z0-9]{0,63}$/),
  label: z.string(),
  description: z.string(),
  placeholder: z.string(),
  /** Anchored regular expression the value must match (no URL syntax can slip through). */
  pattern: z.string(),
  /** A valid value, used to check the catalog entry at startup. */
  example: z.string(),
});
export type MarketplaceParameter = z.infer<typeof marketplaceParameterSchema>;

/**
 * A plugin offered in the marketplace: listing metadata plus the manifest that gets installed.
 * The manifest is a template — `{{param.<key>}}` placeholders, and stdio commands given as a bare
 * name (`npx`) that is resolved against the server's MCP_STDIO_ALLOWLIST at install time — so it
 * is validated as a `pluginManifestSchema` only once rendered.
 */
export const marketplacePluginSchema = z.object({
  /** The manifest name (also the installed plugin's name). */
  id: z.string(),
  name: z.string(),
  publisher: z.string(),
  /** Marked as published by a verified vendor or the platform team. */
  verified: z.boolean().default(false),
  category: marketplaceCategorySchema,
  summary: z.string(),
  description: z.string(),
  tags: z.array(z.string()).default([]),
  homepage: z.url().optional(),
  /** A Simple Icons slug for the brand logo (https://simpleicons.org), when it has one. */
  icon: z
    .string()
    .regex(/^[a-z0-9]+$/)
    .optional(),
  /** Brand colour for the monogram shown when there is no logo. */
  brandColor: hexColor.optional(),
  /** Environment variables the plugin reads (set them on the Environment page). */
  requiredEnv: z
    .array(
      z.object({
        key: z.string(),
        description: z.string(),
        secret: z.boolean(),
        optional: z.boolean().default(false),
      }),
    )
    .default([]),
  parameters: z.array(marketplaceParameterSchema).default([]),
  manifest: z.custom<z.input<typeof pluginManifestSchema>>(
    (v) => typeof v === 'object' && v !== null,
  ),
});
export type MarketplacePlugin = z.input<typeof marketplacePluginSchema>;

/** Body of the marketplace install call; optional, as most entries take no parameters. */
export const marketplaceInstallRequestSchema = z.object({
  parameters: z.record(z.string(), z.string().max(500)).default({}),
});
export type MarketplaceInstallRequest = z.infer<typeof marketplaceInstallRequestSchema>;

export const marketplaceEntryDtoSchema = z.object({
  id: z.string(),
  name: z.string(),
  publisher: z.string(),
  verified: z.boolean(),
  category: marketplaceCategorySchema,
  summary: z.string(),
  description: z.string(),
  tags: z.array(z.string()),
  homepage: z.string().nullable(),
  icon: z.string().nullable(),
  brandColor: z.string().nullable(),
  version: z.string(),
  parameters: z.array(marketplaceParameterSchema.omit({ example: true })),
  /** Why this server can't install the plugin (e.g. `npx` is not on MCP_STDIO_ALLOWLIST), or null. */
  unavailable: z.string().nullable(),
  requiredEnv: z.array(
    z.object({
      key: z.string(),
      description: z.string(),
      secret: z.boolean(),
      optional: z.boolean(),
      /** Whether a tenant-wide variable with this key exists. */
      configured: z.boolean(),
    }),
  ),
  contents: z.object({
    tools: z.array(z.object({ name: z.string(), description: z.string() })),
    skills: z.array(z.object({ name: z.string(), description: z.string() })),
    mcpServers: z.array(z.object({ name: z.string(), description: z.string() })),
  }),
  /** The installed plugin, when this tenant has one with the same name. */
  installed: z.object({ id: z.uuid(), version: z.string() }).nullable(),
});
export type MarketplaceEntryDto = z.infer<typeof marketplaceEntryDtoSchema>;
